// Apply event branding by overwriting CSS variables on :root. Called by every
// surface as soon as the event doc is loaded; safe to call repeatedly.

import type { EventBranding } from "../types";

export interface Rgb { r: number; g: number; b: number; }

// Parse "#rrggbb" / "#rgb" / "rgb(r,g,b)" / "r g b" into an Rgb. Returns null
// when the input can't be understood so callers can fall through gracefully.
export function parseColor(input: string): Rgb | null {
  const s = input.trim();
  if (s.startsWith("#")) {
    const hex = s.slice(1);
    const full =
      hex.length === 3
        ? hex.split("").map((c) => c + c).join("")
        : hex.padEnd(6, "0");
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return null;
    return { r, g, b };
  }
  const m = s.match(/(\d+)\D+(\d+)\D+(\d+)/);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };
  return null;
}

// Accepts "#rrggbb", "rgb(r,g,b)" or "r g b" — returns "r g b" suitable for
// our CSS variable convention (which expects the space-separated triplet
// because Tailwind composes alpha on top).
function toRgbTriplet(input: string): string {
  const c = parseColor(input);
  return c ? `${c.r} ${c.g} ${c.b}` : input.trim();
}

const triplet = (c: Rgb): string => `${Math.round(c.r)} ${Math.round(c.g)} ${Math.round(c.b)}`;

// Relative luminance, 0 (black) .. 1 (white). The simple sRGB-weighted form
// is plenty for a light/dark decision — we don't need full WCAG gamma here.
function luminance(c: Rgb): number {
  return (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
}

// Linear blend of two colours; t=0 → a, t=1 → b.
function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 10, g: 14, b: 26 }; // deep navy-black, matches index.css --bg

export interface DerivedSurfaces {
  bg: string;
  bgElev: string;
  border: string;
  fg: string;
  fgMuted: string;
  isLight: boolean;
}

// Derive a full, contrast-safe surface palette from a single base colour.
// The text colour is always chosen to OPPOSE the background, so no admin
// colour pick can ever produce an unreadable surface. Elevation + border are
// nudged toward white (dark base) or toward white-then-darker (light base)
// so the visual hierarchy — bg < bg-elev, with a visible border — survives
// any hue.
export function deriveSurfaces(base: Rgb): DerivedSurfaces {
  const isLight = luminance(base) >= 0.5;
  if (isLight) {
    return {
      bg: triplet(base),
      // Cards lift toward white; on an already-white base this just stays
      // crisp white, which still reads as "elevated" against an off-white bg.
      bgElev: triplet(mix(base, WHITE, 0.6)),
      border: triplet(mix(base, BLACK, 0.14)),
      fg: triplet(mix(base, BLACK, 0.9)),
      fgMuted: triplet(mix(base, BLACK, 0.55)),
      isLight: true,
    };
  }
  return {
    bg: triplet(base),
    bgElev: triplet(mix(base, WHITE, 0.08)),
    border: triplet(mix(base, WHITE, 0.2)),
    fg: triplet(mix(base, WHITE, 0.95)),
    fgMuted: triplet(mix(base, WHITE, 0.55)),
    isLight: false,
  };
}

// Pick a readable foreground (black or white triplet) for a brand colour —
// used for --primary-fg / --accent-fg so a light brand colour gets dark text
// on its buttons and vice versa.
function readableOn(c: Rgb): string {
  return luminance(c) >= 0.55 ? "18 18 18" : "255 255 255";
}

// Mapping from the EventBranding.zoneColors key → CSS variable name. Kept
// near applyBranding so the two stay in sync when new states are added.
const ZONE_COLOR_VARS = {
  available: "--z-available",
  pending: "--z-pending",
  booked: "--z-booked",
  claiming: "--z-claiming",
} as const;

// CSS-variable names we set/clear here. Used by clearBrandingOverrides() so
// switching events doesn't leak the previous event's customised colours.
const ALL_OVERRIDE_VARS = [
  "--primary",
  "--primary-fg",
  "--accent",
  "--accent-fg",
  "--bg",
  "--bg-elev",
  "--fg",
  "--fg-muted",
  "--border",
  "--z-fill-opacity",
  ...Object.values(ZONE_COLOR_VARS),
];

export function applyBranding(branding: EventBranding | null | undefined): void {
  const root = document.documentElement;
  if (!branding) return;

  // Surface palette. When the event has picked a surfaceBase colour we
  // derive the entire palette from it (and set data-theme from the derived
  // luminance so [data-theme] rules — e.g. --label-stroke — stay aligned).
  // Otherwise fall through to the hand-tuned dark/light theme in index.css.
  const base = branding.surfaceBase ? parseColor(branding.surfaceBase) : null;
  if (base) {
    const s = deriveSurfaces(base);
    root.setAttribute("data-theme", s.isLight ? "light" : "dark");
    root.style.setProperty("--bg", s.bg);
    root.style.setProperty("--bg-elev", s.bgElev);
    root.style.setProperty("--border", s.border);
    root.style.setProperty("--fg", s.fg);
    root.style.setProperty("--fg-muted", s.fgMuted);
  } else {
    root.setAttribute("data-theme", branding.theme);
    // Clear any surface overrides a previously-loaded event may have set.
    for (const v of ["--bg", "--bg-elev", "--border", "--fg", "--fg-muted"]) {
      root.style.removeProperty(v);
    }
  }

  if (branding.primaryColor) {
    const c = parseColor(branding.primaryColor);
    root.style.setProperty("--primary", toRgbTriplet(branding.primaryColor));
    // Auto-derive the on-primary text colour so a light brand colour still
    // gets legible (dark) button text.
    if (c) root.style.setProperty("--primary-fg", readableOn(c));
  }
  if (branding.accentColor) {
    const c = parseColor(branding.accentColor);
    root.style.setProperty("--accent", toRgbTriplet(branding.accentColor));
    if (c) root.style.setProperty("--accent-fg", readableOn(c));
  }
  // Per-event zone state colours. Any unset state falls through to the
  // global default baked into index.css — we explicitly REMOVE the override
  // when a value is absent so that switching events doesn't bleed colours
  // from the previously-loaded event.
  const zc = branding.zoneColors ?? {};
  for (const key of Object.keys(ZONE_COLOR_VARS) as Array<keyof typeof ZONE_COLOR_VARS>) {
    const value = zc[key];
    const varName = ZONE_COLOR_VARS[key];
    if (value) {
      root.style.setProperty(varName, toRgbTriplet(value));
    } else {
      root.style.removeProperty(varName);
    }
  }
  // Per-event zone-fill opacity multiplier. Stored as a plain number; the
  // ZoneOverlay fill / stroke values reference it via CSS calc(). When
  // unset, we remove the var so the default (1, in CSS) takes effect.
  if (typeof branding.zoneOpacity === "number") {
    const clamped = Math.max(0, Math.min(1, branding.zoneOpacity));
    root.style.setProperty("--z-fill-opacity", String(clamped));
  } else {
    root.style.removeProperty("--z-fill-opacity");
  }
}

/** Remove every branding-override CSS variable. Useful for the admin panel
 * when unmounting an event-specific editor and returning to a neutral surface. */
export function clearBrandingOverrides(): void {
  const root = document.documentElement;
  for (const v of ALL_OVERRIDE_VARS) root.style.removeProperty(v);
}

/** Allow embedders to override the theme via `?theme=` on the public live view. */
export function applyThemeOverride(override: "light" | "dark" | null): void {
  if (!override) return;
  document.documentElement.setAttribute("data-theme", override);
}
